import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getBooks, deleteBook } from './_request';
import { IMAGE_URL } from '../../constants/Consts';
import CircularProgress from '@mui/material/CircularProgress';

const Books = () => {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { authToken, themeMode } = useAuth();
  const isDark = themeMode === 'dark';
  const navigate = useNavigate();
  const IMAGE_SHOW = `${IMAGE_URL}matbuaatImages`;

  const fetchBooks = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getBooks(authToken);
      setBooks(response.Books || []);
      setError(null);
    } catch (err) {
      setError('Failed to load books. Please try again later.');
      console.error('Error fetching books:', err);
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    fetchBooks();
  }, [authToken, fetchBooks]);

  const handleEdit = (id) => {
    navigate('/books/editBook', { state: { bookId: id } });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this book?')) {
      try {
       const response = await deleteBook(authToken, id);
       if(response.Result){
         setBooks(books.filter((book) => book.id !== id));
       }
      } catch (err) {
        console.error('Error deleting book:', err);
        alert('Failed to delete book. Please try again.');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-12 space-y-3">
        <CircularProgress color="primary" />
        <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Loading books collection...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass-panel p-8 text-center space-y-4 max-w-xl mx-auto">
        <p className="text-rose-500 font-semibold text-sm">{error}</p>
        <button 
          onClick={fetchBooks} 
          className="glass-btn-primary px-5 py-2 text-xs"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="glass-panel p-6 flex justify-between items-center">
        <div>
          <h2 className={`font-extrabold text-2xl sm:text-3xl ${isDark ? 'text-white' : 'text-slate-900'}`}>Book Collection</h2>
          <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Manage library catalog, publications, and available copies</p>
        </div>
        <button
          onClick={() => navigate('/books/createBook')}
          className="glass-btn-primary px-5 py-2.5 text-xs font-semibold"
        >
          + Add New Book
        </button>
      </div>

      {books.length === 0 ? (
        <div className="glass-panel p-12 text-center text-slate-400 text-sm">
          No books found. Start by adding a new book!
        </div>
      ) : (
        <div className="glass-panel overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-full glass-table">
              <thead>
                <tr>
                  <th className="py-3.5 px-4 text-left font-semibold text-xs uppercase tracking-wider">Image</th>
                  <th className="py-3.5 px-4 text-left font-semibold text-xs uppercase tracking-wider">Book Name</th>
                  <th className="py-3.5 px-4 text-left font-semibold text-xs uppercase tracking-wider">Writer</th>
                  <th className="py-3.5 px-4 text-left font-semibold text-xs uppercase tracking-wider">Published Year</th>
                  <th className="py-3.5 px-4 text-left font-semibold text-xs uppercase tracking-wider">Version</th>
                  <th className="py-3.5 px-4 text-left font-semibold text-xs uppercase tracking-wider">Publisher</th>
                  <th className="py-3.5 px-4 text-left font-semibold text-xs uppercase tracking-wider">Copies</th>
                  <th className="py-3.5 px-4 text-center font-semibold text-xs uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {books.map((book) => (
                  <tr key={book.id} className="transition-colors hover:bg-white/5">
                    <td className="py-3 px-4">
                      {book.image ? (
                        <img 
                          src={`${IMAGE_SHOW}/${book.image}`} 
                          alt={book.bookName} 
                          className="w-14 h-14 object-cover rounded-lg border border-white/10 shadow-sm" 
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-lg bg-slate-800/60 border border-white/10 flex items-center justify-center text-xs text-slate-500">No Cover</div>
                      )}
                    </td>
                    <td className="py-3 px-4 text-xs font-semibold">{book.bookName}</td>
                    <td className="py-3 px-4 text-xs">{book.writerName}</td>
                    <td className="py-3 px-4 text-xs font-mono">{book.publishYear}</td>
                    <td className="py-3 px-4 text-xs">{book.bookVersion}</td>
                    <td className="py-3 px-4 text-xs">{book.publisherName}</td>
                    <td className="py-3 px-4 text-xs font-bold text-cyan-400">{book.totalBooks}</td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <button 
                          className="glass-btn-secondary px-3 py-1.5 text-xs font-medium"
                          onClick={() => handleEdit(book.id)}
                        >
                          Edit
                        </button>
                        <button 
                          className="glass-btn-danger px-3 py-1.5 text-xs font-medium"
                          onClick={() => handleDelete(book.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Books;